/*
 * BeanBurds: free beans in the cart (gift with purchase), added 2026-10-02.
 *
 * When an eligible machine is in the cart (product metafield eqlp.offer_value
 * is 500 or 250), keep one "Specialty beans, included with your machine" line
 * nested under it, matching the machine's quantity. Shopify removes nested
 * lines together with their parent, so removing the machine removes the beans.
 * The beans line is made free by the automatic Buy X Get Y discounts
 * "Beans included with your machine (AED 500 / AED 250)".
 *
 * Eligibility and the gift variants are read from sections/bb-gwp-state.liquid,
 * so adding an offer value to a new machine makes it eligible with no code change.
 * Does no network work at all unless the cart changes or already holds an
 * eligible machine. Never throws into the page.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const STATE_SECTION = 'bb-gwp-state';
const SOURCE = 'bb-gwp';

let timer = null;
let running = false;
let rerun = false;

function routes() {
  const r = (window.Theme && window.Theme.routes) || {};
  return {
    cart: r.cart_url || '/cart',
    add: r.cart_add_url || '/cart/add.js',
    change: (r.cart_change_url || '/cart/change') + '.js',
    update: (r.cart_update_url || '/cart/update') + '.js',
  };
}

async function readState() {
  const url = `${routes().cart}?sections=${STATE_SECTION}&bbgwp=${Date.now()}`;
  /* No JSON Accept header here: on /cart that would return cart.js instead of the section. */
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`state ${res.status}`);
  const json = await res.json();
  const html = json && json[STATE_SECTION];
  if (!html) throw new Error('state section missing');
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const el = doc.querySelector('script[data-bb-gwp-state]');
  if (!el) throw new Error('state payload missing');
  return JSON.parse(el.textContent);
}

/*
 * Work out the next single step: remove one beans line, or add the missing ones.
 * Pure function. Machine lines are grouped by variant, because when the same
 * machine sits on two cart lines Shopify's reported parent keys stop being
 * reliable; in that case all of its beans lines are removed first (the machine
 * lines then merge back into one) and a single beans line is added for the
 * total quantity.
 */
function plan(state) {
  const giftId = state.giftProductId;
  const tiers = state.tiers || {};
  const lines = state.lines || [];
  const gifts = lines.filter((l) => l.productId === giftId);
  const machines = lines.filter((l) => l.productId !== giftId && tiers[String(l.offer)]);
  const machineKeys = new Set(machines.map((m) => m.key));

  /* Beans lines with no eligible machine above them go first. */
  const orphan = gifts.find((g) => !g.parentKey || !machineKeys.has(g.parentKey));
  if (orphan) return { remove: orphan.key };

  const groups = new Map();
  for (const m of machines) {
    if (!groups.has(m.variantId)) groups.set(m.variantId, []);
    groups.get(m.variantId).push(m);
  }

  const adds = [];
  for (const group of groups.values()) {
    const want = tiers[String(group[0].offer)];
    const keys = new Set(group.map((m) => m.key));
    const total = group.reduce((n, m) => n + m.qty, 0);
    const kids = gifts.filter((g) => keys.has(g.parentKey));
    if (kids.length === 1 && kids[0].variantId === want && kids[0].qty === total) continue;
    if (kids.length) return { remove: kids[0].key };
    adds.push({ id: want, quantity: total, parent_line_key: group[0].key });
  }
  return adds.length ? { adds } : null;
}

async function post(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.status >= 400 || json.errors) {
    throw new Error(json.description || json.message || `cart ${res.status}`);
  }
  return json;
}

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return [...ids];
}

function countFromSections(sections) {
  for (const html of Object.values(sections || {})) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector('[ref="cartItemCount"]');
    if (el) return parseInt(el.textContent, 10) || 0;
  }
  return undefined;
}

async function reconcile() {
  const r = routes();
  const sectionIds = cartSectionIds();
  const withSections = sectionIds.length ? { sections: sectionIds.join(','), sections_url: window.location.pathname } : {};
  let first = null;
  let result = null;

  /* Line keys move whenever a nested line is added or removed, so take one
     step at a time and re-read the cart before the next one. */
  for (let pass = 0; pass < 8; pass++) {
    const step = plan(await readState());
    if (!step) break;
    if (step.remove) {
      await post(r.change, { id: step.remove, quantity: 0 });
      first = first || { id: String(step.remove), quantity: 0 };
      continue;
    }
    /* Adds are always the last step of a plan, so ask for the cart sections here. */
    const added = await post(r.add, { items: step.adds, ...withSections });
    first = first || { id: String(step.adds[0].parent_line_key), quantity: step.adds[0].quantity };
    result = { cart: null, sections: added.sections || {}, items: added.items };
    result.itemCount = countFromSections(result.sections);
    break;
  }
  if (!first) return;

  if (!result) {
    /* Removals only: one read that returns the whole cart plus fresh sections. */
    const cart = await post(r.update, withSections);
    result = {
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      sections: cart.sections || {},
      items: cart.items,
      itemCount: cart.item_count,
    };
  }

  /* On page load the header icon may not be listening yet; only wait if it exists. */
  if (document.querySelector('cart-icon') && !customElements.get('cart-icon')) {
    await Promise.race([customElements.whenDefined('cart-icon'), new Promise((done) => setTimeout(done, 2000))]);
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [first],
      promise: deferred.promise,
      detail: { source: SOURCE },
    })
  );
  deferred.resolve({
    cart: result.cart,
    detail: {
      sections: result.sections,
      items: result.items,
      itemCount: result.itemCount,
      source: SOURCE,
      didError: false,
    },
  });
}

async function run() {
  if (running) {
    rerun = true;
    return;
  }
  running = true;
  try {
    await reconcile();
  } catch (error) {
    console.warn('[bb-gwp]', error);
  } finally {
    running = false;
  }
  if (rerun) {
    rerun = false;
    schedule(0);
  }
}

function schedule(delay = 50) {
  clearTimeout(timer);
  timer = setTimeout(run, delay);
}

function isOurs(detail) {
  return Boolean(detail && detail.source === SOURCE);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (isOurs(event.detail)) return;
  if (!event.promise) {
    schedule();
    return;
  }
  event.promise
    .then((result) => {
      if (isOurs(result && result.detail)) return;
      schedule();
    })
    .catch(() => {});
});

/* Safety net for add-to-cart paths that open the drawer without a standard event. */
document.getElementById('cart-drawer')?.addEventListener(DrawerOpenEvent.eventName, () => schedule(0));

if (window.BB_GWP_CHECK) schedule(0);
